import React, { useCallback, useEffect, useState } from "react";
import { ArchiveRestore, CheckCircle2, Clock, FileSpreadsheet, History, Loader2, RefreshCw, RotateCcw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { PaginationBar, TableRowsSkeleton } from "./PaginationBar";
import { UndoBatchDialog, fmtLeft } from "./UndoBatchDialog";
import { Checkbox } from "./ui/checkbox";
import { BatchLogSelectionBar, DeleteBatchLogsDialog } from "./DeleteBatchLogsDialog";
import { api, errMsg, fmtDateIST, fmtTimeIST } from "../lib/api";
import { deleteBatchLogs, logKey, parseLogKey, useBatchLogAccess } from "../lib/batchLogs";
import { ImportReconciliationDialog, ReconcileButton } from "./upload/ImportReconciliationDialog";   // Phase 212

// Phase 145 — one history for the three Demand Upload bulk operations, with a 24-hour Undo per batch.
const TYPES = [
  ["all", "All"],
  ["excel", "Bulk Excel Upload"],
  ["bulk_entry", "Bulk Entry"],
  ["bulk_update", "Bulk Update"],
];
const TYPE_TONE = {
  excel: "bg-primary/10 text-primary border-primary/20",
  bulk_entry: "bg-teal-50 text-teal-700 border-teal-200",
  bulk_update: "bg-violet-50 text-violet-800 border-violet-200",
};
const STATUS_META = {
  completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  cancelled: { label: "Stopped", cls: "bg-amber-50 text-amber-800 border-amber-200", icon: XCircle },
  failed: { label: "Failed", cls: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
  processing: { label: "Processing", cls: "bg-primary/10 text-primary border-primary/20", icon: Loader2 },
  applying: { label: "Applying", cls: "bg-primary/10 text-primary border-primary/20", icon: Loader2 },
  mapping: { label: "Not processed", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: Clock },
  validating: { label: "Not processed", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: Clock },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status || "-", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: Clock };
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={`text-[10px] gap-1 ${m.cls}`}>
      <Icon className={`h-2.5 w-2.5 ${status === "processing" || status === "applying" ? "animate-spin" : ""}`} /> {m.label}
    </Badge>
  );
};

export const BatchHistorySection = ({ code, readOnly = false }) => {
  const [type, setType] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [undoFor, setUndoFor] = useState(null);
  const [recon, setRecon] = useState(null);   // Phase 212: {source, batch_id, filename} → Import Reconciliation Report
  // Phase 151: Super Admin (or a Municipal Admin the Super Admin allowed) can remove batch records from this history.
  // Only the log entry goes — nothing the batch imported is touched.
  const { canDelete: canDeleteLogs } = useBatchLogAccess(code);
  const canDelete = canDeleteLogs && !readOnly;   // Phase 152: "View only" never deletes batch records
  const [selected, setSelected] = useState(() => new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return api.get(`/t/${code}/upload-reports/batch-history`, { params: { type, page, limit } })
      .then((r) => setData(r.data))
      .catch((e) => { setError(errMsg(e)); toast.error(errMsg(e)); })
      .finally(() => setLoading(false));
  }, [code, type, page, limit]);
  useEffect(() => { load(); }, [load]);

  // keep the countdowns + running undos fresh
  useEffect(() => {
    const t = setInterval(() => { load(); }, 60000);
    return () => clearInterval(t);
  }, [load]);

  const items = data?.items || [];
  useEffect(() => { setSelected(new Set()); }, [type, page, limit]);   // selection never survives a filter / page change

  const selectable = items.filter((b) => b.log_deletable);
  const allSelected = selectable.length > 0 && selectable.every((b) => selected.has(logKey(b.type, b.batch_id)));
  const someSelected = selectable.some((b) => selected.has(logKey(b.type, b.batch_id)));
  const toggleAll = (on) => setSelected((prev) => {
    const n = new Set(prev);
    selectable.forEach((b) => { const k = logKey(b.type, b.batch_id); if (on) n.add(k); else n.delete(k); });
    return n;
  });
  const toggleOne = (b, on) => setSelected((prev) => { const n = new Set(prev); const k = logKey(b.type, b.batch_id); if (on) n.add(k); else n.delete(k); return n; });
  const selectedItems = [...selected].map((k) => { const { kind, id } = parseLogKey(k); const b = items.find((x) => x.type === kind && x.batch_id === id); return { kind, id, filename: b?.filename }; });

  return (
    <Card className="shadow-sm" data-testid="batch-history-section">
      <CardContent className="p-0">
        <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-medium text-sm flex items-center gap-1.5"><History className="h-4 w-4" /> Batch History</p>
            <p className="text-[11px] text-muted-foreground">
              Every Bulk Excel Upload, Bulk Entry and Bulk Update batch. Undo reverses a batch for
              <span className="font-medium"> {data?.window_hours || 24} hours</span> after it was processed — properties with payments are always kept.
              <span className="font-medium"> Reconcile</span> compares an import file with what is in the database now.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex flex-wrap gap-1.5" data-testid="batch-history-type-filter">
              {TYPES.map(([v, l]) => (
                <button key={v} type="button" onClick={() => { setType(v); setPage(1); }} aria-pressed={type === v}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1.5 ${type === v
                    ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground hover:bg-muted"}`}
                  data-testid={`batch-history-filter-${v}`}>
                  {l}
                  {data?.counts_by_type && (
                    <span className={`tabular-nums text-[10px] font-semibold rounded-full px-1.5 py-px ${type === v ? "bg-primary-foreground/20" : "bg-muted"}`}>
                      {v === "all" ? Object.values(data.counts_by_type).reduce((a, b) => a + b, 0) : data.counts_by_type[v]}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" className="h-8" onClick={load} disabled={loading} data-testid="batch-history-refresh">
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            {canDelete && (
              <BatchLogSelectionBar count={selected.size} onClear={() => setSelected(new Set())} onDelete={() => setDeleteOpen(true)}
                disabled={loading} testId="batch-history" />
            )}
          </div>
        </div>

        {!data && !error ? <TableRowsSkeleton rows={5} cols={9} testId="batch-history-skeleton" /> : error && !data ? (
          <div className="p-6 text-sm text-center space-y-2" data-testid="batch-history-error">
            <p className="text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={load}>Retry</Button>
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center" data-testid="batch-history-empty">
            <FileSpreadsheet className="h-8 w-8 mx-auto text-muted-foreground/60" />
            <p className="text-sm font-medium mt-2">No batches yet</p>
            <p className="text-xs text-muted-foreground">Processed uploads appear here with their results and a 24-hour Undo.</p>
          </div>
        ) : (
          <div className={`overflow-x-auto ${loading ? "opacity-60 transition-opacity" : ""}`}>
            <Table data-testid="batch-history-table">
              <TableHeader>
                <TableRow>
                  {canDelete && (
                    <TableHead className="w-10">
                      <Checkbox checked={allSelected ? true : someSelected ? "indeterminate" : false} onCheckedChange={(on) => toggleAll(on === true)}
                        disabled={selectable.length === 0} aria-label="Select all batches on this page" data-testid="batch-history-select-all" />
                    </TableHead>
                  )}
                  <TableHead>Date</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Type / File</TableHead>
                  <TableHead>Uploaded by</TableHead>
                  <TableHead className="text-center">Total rows</TableHead>
                  <TableHead className="text-center">Successful</TableHead>
                  <TableHead className="text-center">Failed</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Reconcile</TableHead>
                  <TableHead className="text-right">Undo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((b) => {
                  const e = b.undo_eligibility || {};
                  const undone = b.undo?.status === "completed";
                  const undoing = b.undo?.status === "processing";
                  const when = b.finished_at || b.created_at;
                  return (
                    <TableRow key={`${b.type}-${b.batch_id}`} className={selected.has(logKey(b.type, b.batch_id)) ? "bg-primary/5" : undone ? "bg-muted/30" : ""}
                      data-state={selected.has(logKey(b.type, b.batch_id)) ? "selected" : undefined}
                      data-testid={`batch-history-row-${b.batch_id}`} data-type={b.type} data-undone={undone ? "true" : "false"}>
                      {canDelete && (
                        <TableCell className="w-10">
                          <Checkbox checked={selected.has(logKey(b.type, b.batch_id))} onCheckedChange={(on) => toggleOne(b, on === true)}
                            disabled={!b.log_deletable} aria-label={`Select batch ${b.filename || b.batch_id}`}
                            title={b.log_deletable ? undefined : (b.log_delete_reason === "processing" ? "Still processing — cannot be removed yet" : b.log_delete_reason === "undo_running" ? "Undo is running" : "Cannot be removed yet")}
                            data-testid={`batch-history-select-${b.batch_id}`} />
                        </TableCell>
                      )}
                      <TableCell className="text-xs whitespace-nowrap" data-testid={`batch-date-${b.batch_id}`}>{fmtDateIST(when)}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap tabular-nums" data-testid={`batch-time-${b.batch_id}`}>{fmtTimeIST(when)} IST</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] ${TYPE_TONE[b.type] || ""}`} data-testid={`batch-type-${b.batch_id}`}>{b.type_label}</Badge>
                        <p className="text-xs truncate max-w-[220px] mt-0.5" title={b.filename}>{b.filename || "-"}</p>
                        {b.fy_label && <span className="text-[10px] text-muted-foreground">{b.fy_label}</span>}
                      </TableCell>
                      <TableCell className="text-xs max-w-[160px] truncate" title={b.by} data-testid={`batch-by-${b.batch_id}`}>{b.by || "-"}</TableCell>
                      <TableCell className="text-center text-sm tabular-nums" data-testid={`batch-total-${b.batch_id}`}>{b.total_rows ?? "-"}</TableCell>
                      <TableCell className="text-center text-sm tabular-nums font-medium text-emerald-700" data-testid={`batch-success-${b.batch_id}`}>
                        {b.success ?? "-"}
                        {b.type === "excel" && b.created != null && b.updated != null && (
                          <p className="text-[10px] font-normal text-muted-foreground">{b.created} new · {b.updated} updated</p>
                        )}
                        {b.type === "bulk_update" && b.field_changes != null && (
                          <p className="text-[10px] font-normal text-muted-foreground">{b.field_changes} field{b.field_changes === 1 ? "" : "s"} changed</p>
                        )}
                      </TableCell>
                      <TableCell className={`text-center text-sm tabular-nums font-medium ${b.failed ? "text-red-700" : "text-muted-foreground"}`}
                        data-testid={`batch-failed-${b.batch_id}`}>
                        {b.failed ?? "-"}
                        {b.type === "bulk_entry" && b.duplicates > 0 && (
                          <p className="text-[10px] font-normal text-muted-foreground">{b.duplicates} duplicate{b.duplicates === 1 ? "" : "s"}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          <StatusBadge status={b.status} />
                          {undone && (
                            <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-800 border-violet-200 gap-0.5"
                              title={`Undone by ${b.undo.by_name || "admin"}${b.undo.finished_at ? ` · ${fmtDateIST(b.undo.finished_at)} ${fmtTimeIST(b.undo.finished_at)}` : ""}`}
                              data-testid={`batch-undone-${b.batch_id}`}>
                              <ArchiveRestore className="h-2.5 w-2.5" /> Undone{b.undo.by_name ? ` · ${b.undo.by_name}` : ""}
                            </Badge>
                          )}
                          {undoing && (
                            <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/20 animate-pulse"
                              data-testid={`batch-undo-running-${b.batch_id}`}>Undo running…</Badge>
                          )}
                          {b.undo?.status === "failed" && (
                            <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200" title={b.undo.error}>Undo failed</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {(b.type === "excel" || b.type === "bulk_entry") && ["completed", "cancelled", "failed"].includes(b.status) ? (
                          <ReconcileButton className="h-7 text-xs" testId={`batch-reconcile-button-${b.batch_id}`}
                            onClick={() => setRecon({ source: b.type === "excel" ? "excel" : "third_party", batch_id: b.batch_id, filename: b.filename })} />
                        ) : (
                          <span className="text-[10px] text-muted-foreground" data-testid={`batch-reconcile-unavailable-${b.batch_id}`}>
                            {b.type === "bulk_update" ? "—" : "After processing"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {e.eligible && readOnly ? (
                          <span className="text-[10px] text-muted-foreground block text-right" data-testid={`batch-undo-readonly-${b.batch_id}`}>View only</span>
                        ) : e.eligible ? (
                          <div className="flex flex-col items-end gap-0.5">
                            <Button variant="outline" size="sm" onClick={() => setUndoFor(b)}
                              className="h-7 text-xs text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                              data-testid={`batch-undo-button-${b.batch_id}`}>
                              <RotateCcw className="h-3 w-3 mr-1" /> Undo
                            </Button>
                            <span className="text-[10px] text-muted-foreground tabular-nums inline-flex items-center gap-0.5" data-testid={`batch-undo-left-${b.batch_id}`}>
                              <Clock className="h-2.5 w-2.5" /> {fmtLeft(e.seconds_left)} left
                            </span>
                          </div>
                        ) : undoing ? (
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setUndoFor(b)} data-testid={`batch-undo-view-${b.batch_id}`}>
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" /> View progress
                          </Button>
                        ) : (
                          <span className="text-[10px] text-muted-foreground block max-w-[180px] ml-auto text-right" title={e.reason_text}
                            data-testid={`batch-undo-unavailable-${b.batch_id}`} data-reason={e.reason}>
                            {e.reason === "expired" ? "Undo window closed" : e.reason === "already_undone" ? "—"
                              : e.reason === "no_journal" ? "Not undoable (before tracking)" : e.reason === "nothing_to_undo" ? "Nothing to undo"
                              : e.reason === "processing" ? "Still processing" : e.reason === "not_processed" ? "Never processed"
                              : e.reason_text || "Unavailable"}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {data && data.total > 0 && (
          <div className="px-4 pb-3">
            <PaginationBar page={page} limit={limit} total={data.total} loading={loading}
              onPageChange={setPage} onLimitChange={(n) => { setLimit(n); setPage(1); }} testId="batch-history-pagination" />
          </div>
        )}
      </CardContent>
      <UndoBatchDialog code={code} batch={undoFor} open={!!undoFor} onOpenChange={(v) => { if (!v) setUndoFor(null); }}
        onDone={() => load()} />
      {recon && (
        <ImportReconciliationDialog code={code} source={recon.source} batchId={recon.batch_id} filename={recon.filename}
          open={!!recon} onOpenChange={(v) => { if (!v) setRecon(null); }} testIdPrefix="bh-recon" />
      )}
      {canDelete && (
        <DeleteBatchLogsDialog open={deleteOpen} onOpenChange={setDeleteOpen} items={selectedItems}
          request={(its) => deleteBatchLogs(code, its)} onDone={() => { setSelected(new Set()); load(); }} testId="batch-history-delete" />
      )}
    </Card>
  );
};

export default BatchHistorySection;
